import { _isNil } from '@/littledash';
import { StringUtils } from '@/utils/String.utils';
import { getViewportForBounds, type Edge, type Rect, type Viewport } from '@xyflow/react';
import type {
  GraphCanvasFitOptions,
  GraphCanvasFitViewOptions,
  GraphCanvasNode,
  GraphCanvasSize,
  LayoutFn,
  SiblingComparator,
} from './GraphCanvas.model';

const LEVEL_HEIGHT = 120;
const SIBLING_WIDTH = 360;

/** React Flow's own viewport zoom bounds (its `minZoom`/`maxZoom` defaults), declared explicitly so the
 *  zoom computed here and the zoom the instance is willing to apply cannot drift apart. */
export const MIN_ZOOM = 0.5;
export const MAX_ZOOM = 2;

/** React Flow's fitView padding default, so omitting `padding` fits as `fitView` would have. */
const DEFAULT_PADDING = 0.1;

/** Fit animation duration when the caller's `fitViewOptions` doesn't specify one. */
export const FIT_DURATION_MS = 200;

/** Resolves caller options against the measured canvas; a plain object passes straight through. */
export const resolveFitViewOptions = (
  options: GraphCanvasFitViewOptions | undefined,
  size: GraphCanvasSize
): GraphCanvasFitOptions => (typeof options === 'function' ? options(size) : (options ?? {}));

/**
 * The viewport that fits `bounds` into `canvas`. Pure wrapper around React Flow's own
 * `getViewportForBounds`, adding optional top alignment.
 *
 * `getViewportForBounds` always centres the graph, and honours asymmetric padding only when that padding
 * exceeds the slack around a centred fit — so padding alone cannot top-align a graph much shorter than
 * its canvas. When `topAlignInsetPx` is given, solve for the y that puts the bounds' top edge at that
 * inset and take whichever sits higher.
 *
 * The crossover is exact when `padding.top === padding.bottom` (asymmetric vertical padding shifts the
 * centred y and moves it): centring wins once the graph's rendered height exceeds
 * `canvas.height - 2 * topAlignInsetPx`, so a graph that overflows spills evenly off both edges rather
 * than off the bottom only. In the intended usage — where the caller's `padding.top`/`padding.bottom`
 * equal the inset — that boundary coincides with the graph no longer fitting, because the same inset has
 * already been excluded from the height the fit had to work with.
 */
export const fitViewport = (
  bounds: Rect,
  canvas: GraphCanvasSize,
  options: GraphCanvasFitOptions,
  topAlignInsetPx?: number
): Viewport => {
  const { padding = DEFAULT_PADDING } = options;
  // Bound the caller's zoom range by the instance's own. Note this is policy, not a correction: `setViewport`
  // applies whatever zoom it is given — d3's `zoom.transform` assigns verbatim and enforces `scaleExtent`
  // only for `scaleTo`/`scaleBy`/wheel/pinch — so an out-of-range zoom would be applied, and correctly
  // centred, rather than silently clamped. It is still wrong to hand back a zoom outside the bounds the
  // canvas declares on the instance, so intersect the two ranges here.
  //
  // Both are clamped into [MIN_ZOOM, MAX_ZOOM] and then min against max, so an intersection that would
  // otherwise invert (e.g. a caller's `maxZoom: 0.3` against a MIN_ZOOM of 0.5) cannot return a zoom
  // outside the declared bounds — React Flow's own `clamp` lets `max` win when `min > max`.
  const maxZoom = Math.min(Math.max(options.maxZoom ?? MAX_ZOOM, MIN_ZOOM), MAX_ZOOM);
  const minZoom = Math.min(Math.max(options.minZoom ?? MIN_ZOOM, MIN_ZOOM), maxZoom);
  const { x, y, zoom } = getViewportForBounds(bounds, canvas.width, canvas.height, minZoom, maxZoom, padding);
  return { x, zoom, y: _isNil(topAlignInsetPx) ? y : Math.min(y, topAlignInsetPx - bounds.y * zoom) };
};

/** Default sibling order: by node id, locale-independent so it is stable across environments. */
const compareById: SiblingComparator<Record<string, unknown>> = (a, b) => StringUtils.compareLexicographic(a.id, b.id);

/**
 * A deterministic top-down tree layout for `GraphCanvasEngine`. Depth (y) is each node's distance
 * from a root (a node with no incoming edge) computed via the edge set; horizontal position (x)
 * comes from `compareSiblings` (defaults to node id).
 *
 * Determinism guarantee: the same set of nodes+edges always produces the same positions, and the
 * relative order of same-level nodes is stable (fixed by `compareSiblings`). Adding or removing a
 * node can change existing nodes' indices and recentres its level — so this is not absolute-position
 * stability. Positions are a pure function of the (nodes, edges, comparator) input, independent of the
 * order edges are supplied.
 *
 * Pure: returns new node objects and does not mutate the inputs. Prefer `treeLayout(compareSiblings)`
 * to bind a domain comparator into a `LayoutFn` for the engine config.
 */
export const layoutTree = <TData extends Record<string, unknown>>(
  nodes: ReadonlyArray<GraphCanvasNode<TData>>,
  edges: ReadonlyArray<Edge>,
  compareSiblings: SiblingComparator<TData> = compareById
): Array<GraphCanvasNode<TData>> => {
  // A node with multiple incoming edges resolves to its lowest-id parent, so depth is a pure function
  // of the edge set — independent of the order edges are supplied (keeps the layout deterministic
  // even when the edge set is not strictly a tree).
  const parentByChild = new Map<string, string>();
  edges.forEach((edge) => {
    const existing = parentByChild.get(edge.target);
    if (_isNil(existing) || edge.source < existing) {
      parentByChild.set(edge.target, edge.source);
    }
  });

  const depthOf = (nodeId: string): number => {
    let depth = 0;
    let current = nodeId;
    const guard = new Set<string>();
    while (parentByChild.has(current) && !guard.has(current)) {
      guard.add(current);
      current = parentByChild.get(current)!;
      depth += 1;
    }
    return depth;
  };

  const levels = nodes.reduce<Map<number, Array<GraphCanvasNode<TData>>>>((acc, node) => {
    const depth = depthOf(node.id);
    const bucket = acc.get(depth);
    if (_isNil(bucket)) {
      acc.set(depth, [node]);
    } else {
      bucket.push(node);
    }
    return acc;
  }, new Map());

  const positionById = new Map<string, { x: number; y: number }>();
  levels.forEach((levelNodes, depth) => {
    const ordered = [...levelNodes].sort(compareSiblings);
    const offset = ((ordered.length - 1) * SIBLING_WIDTH) / 2;
    ordered.forEach((node, index) => {
      positionById.set(node.id, { x: index * SIBLING_WIDTH - offset, y: depth * LEVEL_HEIGHT });
    });
  });

  return nodes.map((node) => ({ ...node, position: positionById.get(node.id) ?? { x: 0, y: 0 } }));
};

/**
 * Binds a sibling comparator into a `LayoutFn` for the engine config, so a consumer can order
 * same-level nodes by a domain field. `treeLayout()` (no arg) orders by node id.
 */
export const treeLayout =
  <TData extends Record<string, unknown>>(compareSiblings?: SiblingComparator<TData>): LayoutFn<TData> =>
  (nodes, edges) =>
    layoutTree(nodes, edges, compareSiblings);
